import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

export interface InfoGrant {
  id: string;
  listing_id: string;
  granted_to_user_id: string;
  granted_by_user_id: string;
  granted_fields: string[];
  access_request_id?: string;
  expires_at?: string;
  created_at: string;
}

export const useInfoGrants = (listingId?: string) => {
  const { user } = useAuth();
  const [grants, setGrants] = useState<InfoGrant[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGrants = async () => {
    if (!listingId || !user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('info_access_grants' as any)
        .select('*')
        .eq('listing_id', listingId)
        .eq('granted_to_user_id', user.id);

      if (error) throw error;
      setGrants((data as any) || []);
    } catch (error) {
      console.error('Error fetching info grants:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has access to a specific field
   */
  const hasFieldAccess = (field: string): boolean => {
    return grants.some(
      (g) =>
        g.granted_fields.includes(field) &&
        (!g.expires_at || new Date(g.expires_at) > new Date())
    );
  };

  /**
   * Get all granted fields for this listing
   */
  const getGrantedFields = (): string[] => {
    const allFields = grants
      .filter((g) => !g.expires_at || new Date(g.expires_at) > new Date())
      .flatMap((g) => g.granted_fields);
    return [...new Set(allFields)];
  };

  /**
   * Check if user has access to price info
   */
  const hasPriceAccess = (): boolean => hasFieldAccess('price');

  /**
   * Check if user has access to location info
   */
  const hasLocationAccess = (): boolean => hasFieldAccess('location');

  /**
   * Check if user has access to serial/part number
   */
  const hasSerialAccess = (): boolean => hasFieldAccess('serial');

  /**
   * Check if user has access to time/hours data
   */
  const hasHoursAccess = (): boolean => hasFieldAccess('hours');

  /**
   * Check if user has access to email
   */
  const hasEmailAccess = (): boolean => hasFieldAccess('email');

  /**
   * Check if user has access to phone
   */
  const hasPhoneAccess = (): boolean => hasFieldAccess('phone');

  /**
   * Check if user has access to company info
   */
  const hasCompanyAccess = (): boolean => hasFieldAccess('company');

  /**
   * Check if user has access to module details
   */
  const hasModulesAccess = (): boolean => hasFieldAccess('modules');

  /**
   * Check if user has access to shipping info
   */
  const hasShippingAccess = (): boolean => hasFieldAccess('shipping');

  useEffect(() => {
    fetchGrants();

    if (!listingId || !user) return;

    // Real-time subscription
    const channel = supabase
      .channel(`info-grants-${listingId}-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'info_access_grants',
          filter: `listing_id=eq.${listingId}`,
        },
        () => {
          fetchGrants();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [listingId, user?.id]);

  return {
    grants,
    loading,
    hasFieldAccess,
    getGrantedFields,
    hasPriceAccess,
    hasLocationAccess,
    hasSerialAccess,
    hasHoursAccess,
    hasEmailAccess,
    hasPhoneAccess,
    hasCompanyAccess,
    hasModulesAccess,
    hasShippingAccess,
    refetch: fetchGrants,
  };
};
